import { Getter } from '../Getter';
import { HttpError } from '../HttpError';
import { ApiStaleBranches } from './Models/ApiStaleBranches';
import { StaleBranches } from './Models/StaleBranches';
import isValidUUID from 'uuid-validate';
import URI from 'urijs';
import dayjs from 'dayjs';

type ApiResponse = ApiStaleBranches | null | undefined;

interface DataResponse {
  data?: StaleBranches;
  error?: never;
}
interface ErrorResponse {
  data?: never;
  error: HttpError;
}

export type ClientOutput = DataResponse | ErrorResponse;

export class StaleBranchesClient {
  private getter: Getter<ApiResponse | HttpError>;

  constructor(getter: Getter<ApiResponse | HttpError>) {
    this.getter = getter;
  }

  public async retrieveAsync(repositoryUUID: string): Promise<ClientOutput> {
    if (!isValidUUID(repositoryUUID)) {
      // equivalent to the response we'd get on the server
      return {
        error: new HttpError(400),
      };
    }

    const response = await this.tryRetrieveAsync(repositoryUUID);

    if (!response) {
      return {
        data: undefined,
        error: undefined,
      };
    }

    if (response instanceof HttpError) {
      return {
        error: response,
      };
    }

    return {
      data: toStaleBranches(response),
    };
  }

  private async tryRetrieveAsync(
    repositoryUUID: string
  ): Promise<ApiResponse | HttpError> {
    const href = getHref(repositoryUUID);

    return await this.getter.get(href);
  }
}

export function getHref(repositoryUUID: string): string {
  const uri = URI('/api/v1/projects')
    .segment(repositoryUUID)
    .segment('stale-branches');

  return uri.toString();
}

export function toStaleBranches(branches: ApiStaleBranches): StaleBranches {
  return branches.map((branch) => {
    return {
      author: {
        name: branch.author.name,
        email: branch.author.email,
      },
      branchName: branch.branch_name,
      lastCommitDate: dayjs(branch.last_commit_date).toDate(),
      isMerged: branch.merged,
    };
  });
}
